/**
 * FlexiblePurchaseButtonSection — Purchase button editor for FlexibleLayout
 *
 * Same structure as FlexibleButtonSection but with purchase-specific config and defaults.
 * Rendered when a Button section has action.kind === 'purchase' (detected by adapter).
 *
 * Default purchase action: action.kind = 'purchase' (triggers in-app purchase flow).
 * Visual defaults: yellow background (#ffc201), bold text, centered.
 */

import React, { useCallback, useState, useMemo } from 'react';
import { SAccordion } from '@modules/ui-components';
import { Box, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { TextElement } from '../../../factories/elements/TextElement';
import { EditorGuideProvider } from '../../../contexts';
import {
  V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG,
  createDefaultFlexiblePurchaseButton,
} from '../../../config/v2FlexiblePurchaseButtonConfig';
import { SECTION_MARGIN_BOTTOM } from '../../../config/ui';
import { getValueByPath, isFieldEnabled, getV2TranslationValue } from '../../../utils/v2FieldOperations';
import {
  getStyleValues as getStyleValuesUtil,
  getButtonStyleValues as getButtonStyleValuesUtil,
} from '../../../utils/fieldValueOperations';
import { useEditorPresentationProfile } from './PresentationProfileContext';
import {
  getSectionTestId,
  getFieldTestId,
  getCheckboxTestId,
  getStylesToggleTestId,
  getInputTestId,
} from '../../../utils/testIdUtils';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

/** Available parent section for linking */
interface ParentSectionOption {
  id: string;
  label: string;
  kind: string;
}

export interface FlexiblePurchaseButtonSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;
  /** Current linked parent section ID */
  parentSectionId?: string;
  /** Available sections that can be linked as parent */
  availableParentSections?: ParentSectionOption[];
  /** Callback to change the linked parent section */
  onParentSectionChange?: (parentId: string | undefined) => void;
  /** Project ID for icon upload (V2 only) */
  projectId?: string;
  /** Onboarding ID for icon upload (V2 only) */
  onboardingId?: string;
  /** Version ID for icon upload (V2 only) */
  versionId?: string;
}

export const FlexiblePurchaseButtonSection: React.FC<FlexiblePurchaseButtonSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  sectionTitle,
  parentSectionId,
  availableParentSections,
  onParentSectionChange,
  projectId,
  onboardingId,
  versionId,
}) => {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const getFieldValue = useCallback(
    (config: FieldConfig): string => {
      if (config.l10nPath) {
        return getV2TranslationValue(step, config.l10nPath, locale);
      }
      return getValueByPath<string>(step, config.path) ?? '';
    },
    [step, locale],
  );

  const checkFieldEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step],
  );

  const activeProfile = useEditorPresentationProfile();

  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> =>
      getStyleValuesUtil(step as unknown as Record<string, unknown>, config, activeProfile),
    [step, activeProfile],
  );

  const getButtonStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => {
      const result = getButtonStyleValuesUtil(step as unknown as Record<string, unknown>, config, activeProfile);
      // Transform onCancelAction Trigger → string for StyleFieldRow checkbox
      const downsalePath = 'content.buttonContent.action.onCancelAction';
      if (downsalePath in result) {
        result[downsalePath] = result[downsalePath] != null ? 'enabled' : null;
      }
      return result;
    },
    [step, activeProfile],
  );

  const handleFieldChange = useCallback(
    (config: FieldConfig, value: string) => {
      if (config.l10nPath) {
        onChange(`${config.l10nPath}.translations.${locale}`, value);
      } else {
        onChange(config.path, value);
      }
    },
    [onChange, locale],
  );

  const handleEnabledChange = useCallback(
    (config: FieldConfig, enabled: boolean) => {
      if (enabled) {
        // Create purchase TapTarget with action.kind = 'Purchase'
        onChange(config.path, createDefaultFlexiblePurchaseButton(locale));
      } else {
        onChange(config.path, null);
      }
    },
    [onChange, locale],
  );

  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => {
      // Downsale toggle: string → onCancelAction standard Trigger
      if (stylePath.endsWith('.action.onCancelAction')) {
        onChange(stylePath, value != null ? { kind: 'advance', edges: [] } : null);
        return;
      }
      onChange(stylePath, value);
    },
    [onChange],
  );

  const buttonProps = useMemo(
    () => ({
      fieldConfig: V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG,
      value: getFieldValue(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG),
      templateValue: undefined,
      enabled: checkFieldEnabled(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG),
      locale,
      styleValues: getStyleValues(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG),
      buttonStyleValues: getButtonStyleValues(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG),
      expandedStyles: expandedFields.has(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG.path),
    }),
    [getFieldValue, checkFieldEnabled, locale, getStyleValues, getButtonStyleValues, expandedFields],
  );

  const SECTION_NAME = 'Purchase Button';

  return (
    <SAccordion
      title={sectionTitle ?? SECTION_NAME}
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        <TextElement
          {...buttonProps}
          onChange={(value) => handleFieldChange(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
          data-testid={getFieldTestId(SECTION_NAME, V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_FLEXIBLE_PURCHASE_BUTTON_CONFIG.label)}
        />
      </EditorGuideProvider>
      {availableParentSections && availableParentSections.length > 0 && onParentSectionChange && (
        <Box sx={{ mt: 1.5 }} data-testid="select-purchase-button-linked-section">
          <FormControl fullWidth size="small">
            <InputLabel>Linked to section</InputLabel>
            <Select
              value={parentSectionId ?? ''}
              label="Linked to section"
              onChange={(e: SelectChangeEvent) => {
                const val = e.target.value;
                onParentSectionChange(val || undefined);
              }}
              disabled={disabled}
            >
              <MenuItem value="">None</MenuItem>
              {availableParentSections.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </SAccordion>
  );
};
